/**
 * Mail Hub 控制面与用户使用面 API 客户端
 *
 * 所有业务端请求通过 /api/v3/mail-hub/ 完成。
 * v3 apiClient 的 baseURL 已包含 `/api`，因此本文件使用相对 `/v3/mail-hub/**`。
 * 遵循 P11/P12 原则：前端宿主复用，JWT 共享，业务端暴露面收敛到 v3，
 * Mail Hub / Document Center 的资源授权与内部写入边界仍留在各自领域内。
 *
 * → docs/30_outbound_mail/03_auth_api_and_database_boundary.md §3
 */

import { v4 as uuidv4 } from "uuid";

import apiClient from "./client";
import { unwrapV3ResponseData } from "./responseContract";

const MAIL_HUB_PREFIX = "/v3/mail-hub";
const DOC_CENTER_PREFIX = "/v3/mail-hub/doc-center";
export const OUTBOUND_SEND_MODES = {
    single: "single",
    batchIndependent: "batch_independent",
};
const MAIL_HUB_CONTENT_URL_PREFIXES = [
    MAIL_HUB_PREFIX,
    "/internal/mail-hub",
    "/api/v1/mail-hub",
    "/v1/mail-hub",
];

const hasPrefixBoundary = (value, prefix) =>
    value === prefix || value.startsWith(`${prefix}/`) || value.startsWith(`${prefix}?`) || value.startsWith(`${prefix}#`);

const buildCateringFlightContextQuery = (params = {}) => ({
    departure_airport: params.departure_airport ?? params.departureAirport,
    date_from: params.date_from ?? params.dateFrom,
    date_to: params.date_to ?? params.dateTo,
});

export const normalizeMailHubContentUrl = (contentUrl) => {
    if (typeof contentUrl !== "string") {
        throw new Error("Mail Hub 内容 URL 无效");
    }

    const normalizedUrl = contentUrl.trim();
    for (const prefix of MAIL_HUB_CONTENT_URL_PREFIXES) {
        if (hasPrefixBoundary(normalizedUrl, prefix)) {
            return `${MAIL_HUB_PREFIX}${normalizedUrl.slice(prefix.length)}`;
        }
    }

    throw new Error("Mail Hub 内容 URL 必须经由 v3 façade");
};

// ============================================================================
// Composer reference data
// ============================================================================

export const listCateringFlightContext = (params = {}) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/composer/catering/flight-context`, {
        params: buildCateringFlightContextQuery(params),
    });

export const cateringComposerApi = Object.freeze({
    listFlightContext: listCateringFlightContext,
});

// ============================================================================
// 组织
// ============================================================================

export const listOrganizations = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/organizations`);

// ============================================================================
// 邮箱 CRUD
// ============================================================================

export const listMailboxes = (orgId) => {
    const params = orgId ? { org_id: orgId } : {};
    return apiClient.get(`${MAIL_HUB_PREFIX}/mailboxes`, { params });
};

export const getMailbox = (id) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/mailboxes/${id}`);

export const createMailbox = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/mailboxes`, data);

export const updateMailbox = (id, data) =>
    apiClient.patch(`${MAIL_HUB_PREFIX}/mailboxes/${id}`, data);

export const deleteMailbox = (id) =>
    apiClient.delete(`${MAIL_HUB_PREFIX}/mailboxes/${id}`);

// ============================================================================
// 连接与控制动作
// ============================================================================

export const testMailboxConnection = (id) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/mailboxes/${id}/test-connection`);

export const testConnectionWithCredentials = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/mailboxes/test-connection`, data);

export const triggerMailboxSync = (id) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/mailboxes/${id}/trigger-sync`);

// ============================================================================
// 观测聚合
// ============================================================================

export const getMailboxIngestSummary = (id, hours = 24) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/mailboxes/${id}/ingest-summary`, { params: { hours } });

export const getMailboxPendingSummary = (id) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/mailboxes/${id}/pending-summary`);

export const getMailboxBusinessJudgmentAvailability = (id) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/mailboxes/${id}/business-judgment-availability`);

// ============================================================================
// 收件箱用户使用面
// ============================================================================

export const listInboxMailboxes = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/inbox/mailboxes`);

export const listInboxCategories = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/inbox/categories`);

const buildInboxConversationQueryParams = (params = {}) => ({
    mailbox_id: params.mailbox_id ?? params.mailboxId,
    query: params.query,
    category: params.category,
    processing_status: params.processing_status ?? params.processingStatus,
    hasAttachment: params.hasAttachment,
    hasPdfAttachment: params.hasPdfAttachment,
    bja_trigger_status: params.bja_trigger_status ?? params.bjaTriggerStatus,
    page: params.page,
    page_size: params.page_size ?? params.pageSize,
});

export const listInboxConversations = (params = {}) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/inbox/conversations`, { params: buildInboxConversationQueryParams(params) });

export const triggerJudgment = (receiptId) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/inbox/receipts/${receiptId}/trigger-judgment`);

export const getMailThreadContext = (threadId) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/context/threads/${threadId}`);

export const getMailReceiptContext = (receiptId) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/context/receipts/${receiptId}`);

export const getEntityDigest = (entityType, entityId, params = {}) => {
    const query = {
        ...params,
        categories: Array.isArray(params.categories) ? params.categories.join(",") : params.categories,
    };
    return apiClient.get(`${MAIL_HUB_PREFIX}/entity-digest/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, { params: query });
};

export const getInboxRawMessageAccess = (receiptId, disposition = "attachment") =>
    apiClient.get(`${MAIL_HUB_PREFIX}/inbox/receipts/${receiptId}/raw/access`, {
        params: { disposition },
    });

export const getInboxAttachmentAccess = (receiptId, attachmentId, disposition = "attachment") =>
    apiClient.get(`${MAIL_HUB_PREFIX}/inbox/receipts/${receiptId}/attachments/${attachmentId}/access`, {
        params: { disposition },
    });

export const downloadInboxProxyContent = (contentUrl) =>
    apiClient.get(normalizeMailHubContentUrl(contentUrl), {
        responseType: "blob",
        _skipInterceptorProcessing: true,
    });

// ============================================================================
// 发件箱用户使用面
// ============================================================================

export const listSenderMailboxes = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/outbound/sender-mailboxes`);

export const listOutboundHistory = (params = {}) => {
    const query = {
        mailbox_id: params.mailbox_id ?? params.mailboxId,
        page: params.page,
        page_size: params.page_size ?? params.pageSize,
    };
    return apiClient.get(`${MAIL_HUB_PREFIX}/outbound/history`, { params: query });
};

export const presignOutboundAttachmentUpload = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/outbound/attachments/presign`, data);

export const completeOutboundAttachmentUpload = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/outbound/attachments/complete`, data);

const readMailHubEnvelopeData = (response, context) => {
    const payload = unwrapV3ResponseData(response);
    if (payload && typeof payload === "object" && !Array.isArray(payload) && Object.prototype.hasOwnProperty.call(payload, "data")) {
        return payload.data;
    }
    if (payload && typeof payload === "object") {
        return payload;
    }
    throw new Error(`${context} 返回格式不符合 Mail Hub v3 契约`);
};

const buildTemplateListQuery = (params = {}) => ({
    scope: params.scope,
    category: params.category,
    search: params.search,
});

// ============================================================================
// 出站邮件模板
// ============================================================================

export const listTemplates = async (params = {}) => {
    const response = await apiClient.get(`${MAIL_HUB_PREFIX}/outbound/templates`, {
        params: buildTemplateListQuery(params),
    });
    const data = readMailHubEnvelopeData(response, "出站模板列表");
    const items = Array.isArray(data?.items) ? data.items : [];
    const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
    return { items, total };
};

export const getTemplate = async (id) => {
    if (typeof id !== "string" || id.trim().length === 0) {
        throw new Error("模板 ID 必须是非空字符串");
    }
    const response = await apiClient.get(`${MAIL_HUB_PREFIX}/outbound/templates/${encodeURIComponent(id)}`);
    const data = readMailHubEnvelopeData(response, "出站模板详情");
    return data?.template || data;
};

const DRAFT_ADDRESS_FIELDS = ["to_addresses", "cc_addresses", "bcc_addresses"];
const DRAFT_OBJECT_FIELDS = ["template_variables", "business_context", "metadata"];

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const isRecord = (value) => value && typeof value === "object" && !Array.isArray(value);

const requireRecordValue = (value, context) => {
    if (!isRecord(value)) {
        throw new Error(`${context} 必须是对象`);
    }
    return value;
};

const requireStringValue = (value, context) => {
    if (typeof value !== "string" || value.length === 0) {
        throw new Error(`${context} 必须是非空字符串`);
    }
    return value;
};

const requireNullableStringValue = (value, context) => {
    if (value === null || typeof value === "string") {
        return value;
    }
    throw new Error(`${context} 必须是字符串或 null`);
};

const requireNonNegativeIntegerValue = (value, context) => {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`${context} 必须是非负整数`);
    }
    return value;
};

const createOutboundBatchId = () => {
    const cryptoProvider = typeof window !== "undefined" ? window.crypto : null;
    return cryptoProvider?.randomUUID?.() || uuidv4();
};

const normalizeOutboundSendResultItem = (item, index) => {
    const record = requireRecordValue(item, `出站发送结果.results[${index}]`);
    const status = requireStringValue(record.status, `出站发送结果.results[${index}].status`);
    if (!["accepted", "failed"].includes(status)) {
        throw new Error(`出站发送结果.results[${index}].status 不受支持: ${status}`);
    }
    return {
        recipient_address: requireStringValue(record.recipient_address, `出站发送结果.results[${index}].recipient_address`),
        status,
        canonical_message_id: requireNullableStringValue(record.canonical_message_id, `出站发送结果.results[${index}].canonical_message_id`),
        error: requireNullableStringValue(record.error, `出站发送结果.results[${index}].error`),
    };
};

const normalizeOutboundSendResponse = (response, payload) => {
    const data = requireRecordValue(readMailHubEnvelopeData(response, "出站发送结果"), "出站发送结果");
    if (!Array.isArray(data.results)) {
        throw new Error("出站发送结果.results 必须是数组");
    }
    const responseBatchId = data.batch_id === undefined ? null : requireNullableStringValue(data.batch_id, "出站发送结果.batch_id");
    const batchId = responseBatchId || payload.batch_id || null;
    return {
        batch_id: batchId,
        send_mode: payload.send_mode || null,
        results: data.results.map(normalizeOutboundSendResultItem),
    };
};

const normalizeBatchResultItem = (item, index) => {
    const record = requireRecordValue(item, `批次投递结果.items[${index}]`);
    return {
        recipient_address: requireStringValue(record.recipient_address, `批次投递结果.items[${index}].recipient_address`),
        canonical_message_id: requireNullableStringValue(record.canonical_message_id, `批次投递结果.items[${index}].canonical_message_id`),
        delivery_status: requireStringValue(record.delivery_status, `批次投递结果.items[${index}].delivery_status`),
        terminal_reason: requireNullableStringValue(record.terminal_reason, `批次投递结果.items[${index}].terminal_reason`),
        last_error: requireNullableStringValue(record.last_error, `批次投递结果.items[${index}].last_error`),
    };
};

const normalizeBatchResultsResponse = (response) => {
    const data = requireRecordValue(readMailHubEnvelopeData(response, "批次投递结果"), "批次投递结果");
    const summary = requireRecordValue(data.summary, "批次投递结果.summary");
    if (!Array.isArray(data.items)) {
        throw new Error("批次投递结果.items 必须是数组");
    }
    return {
        batch_id: requireStringValue(data.batch_id, "批次投递结果.batch_id"),
        total: requireNonNegativeIntegerValue(data.total, "批次投递结果.total"),
        summary: {
            succeeded: requireNonNegativeIntegerValue(summary.succeeded, "批次投递结果.summary.succeeded"),
            failed: requireNonNegativeIntegerValue(summary.failed, "批次投递结果.summary.failed"),
            in_progress: requireNonNegativeIntegerValue(summary.in_progress, "批次投递结果.summary.in_progress"),
        },
        items: data.items.map(normalizeBatchResultItem),
    };
};

const normalizeDraftAddressField = (value, field) => {
    if (value === undefined || value === null) {
        return [];
    }
    if (!Array.isArray(value)) {
        throw new Error(`${field} 必须是数组`);
    }
    return value;
};

const normalizeDraftObjectField = (value, field) => {
    if (value === undefined || value === null) {
        return {};
    }
    if (!isRecord(value)) {
        throw new Error(`${field} 必须是对象`);
    }
    return value;
};

const buildOutboundDraftPatchPayload = (data = {}, includeMissingDefaults = false) => {
    if (!isRecord(data)) {
        return data;
    }

    const payload = { ...data };
    DRAFT_ADDRESS_FIELDS.forEach((field) => {
        if (includeMissingDefaults || hasOwn(data, field)) {
            payload[field] = normalizeDraftAddressField(data[field], field);
        }
    });
    DRAFT_OBJECT_FIELDS.forEach((field) => {
        if (includeMissingDefaults || hasOwn(data, field)) {
            payload[field] = normalizeDraftObjectField(data[field], field);
        }
    });
    return payload;
};

const buildUpdateOutboundDraftPayload = (data = {}) => {
    if (!isRecord(data)) {
        return data;
    }

    const payload = { ...data };
    if (hasOwn(data, "patch")) {
        payload.patch = buildOutboundDraftPatchPayload(data.patch, false);
    }
    return payload;
};

const buildOutboundSendPayload = (data = {}) => {
    const payload = { ...data };
    const sendMode = data.send_mode ?? data.sendMode;
    if (sendMode !== undefined && sendMode !== null) {
        payload.send_mode = sendMode;
    }
    delete payload.sendMode;
    if (payload.send_mode === OUTBOUND_SEND_MODES.batchIndependent && !payload.batch_id) {
        payload.batch_id = createOutboundBatchId();
    }
    if (data.body && typeof data.body === "object" && !Array.isArray(data.body)) {
        if (payload.body_html === undefined && typeof data.body.html === "string") {
            payload.body_html = data.body.html;
        }
        if (payload.body_text === undefined && typeof data.body.text === "string") {
            payload.body_text = data.body.text;
        }
        delete payload.body;
    }
    if (payload.content_block_manifest === undefined && data.contentBlockManifest !== undefined) {
        payload.content_block_manifest = data.contentBlockManifest;
    }
    delete payload.contentBlockManifest;
    if (payload.attachments === undefined && data.outboundAttachments !== undefined) {
        payload.attachments = data.outboundAttachments;
    }
    delete payload.outboundAttachments;
    return payload;
};

export const sendOutboundMail = async (data) => {
    const payload = buildOutboundSendPayload(data);
    const response = await apiClient.post(`${MAIL_HUB_PREFIX}/outbound/send`, payload);
    return normalizeOutboundSendResponse(response, payload);
};

export const getBatchResults = async (batchId) => {
    if (typeof batchId !== "string" || batchId.trim().length === 0) {
        throw new Error("批次 ID 必须是非空字符串");
    }
    const response = await apiClient.get(`${MAIL_HUB_PREFIX}/outbound/batches/${encodeURIComponent(batchId)}/results`);
    return normalizeBatchResultsResponse(response);
};

export const createOutboundDraft = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/outbound/drafts`, buildOutboundDraftPatchPayload(data, true));

export const getOutboundDraft = (draftId) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/outbound/drafts/${draftId}`);

export const listOutboundDrafts = (params = {}) => {
    const query = {
        page: params.page,
        page_size: params.page_size ?? params.pageSize,
    };
    return apiClient.get(`${MAIL_HUB_PREFIX}/outbound/drafts`, { params: query });
};

export const updateOutboundDraft = (draftId, data) =>
    apiClient.patch(`${MAIL_HUB_PREFIX}/outbound/drafts/${draftId}`, buildUpdateOutboundDraftPayload(data));

export const deleteOutboundDraft = (draftId) =>
    apiClient.delete(`${MAIL_HUB_PREFIX}/outbound/drafts/${draftId}`);

export const listDocCenterSpaces = (params = {}) =>
    apiClient.get(`${DOC_CENTER_PREFIX}/spaces`, { params });

export const listDocCenterFolders = (spaceId, params = {}) =>
    apiClient.get(`${DOC_CENTER_PREFIX}/spaces/${spaceId}/folders`, { params });

// ============================================================================
// 调度器状态
// ============================================================================

export const getSchedulerStatus = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/scheduler/status`);

// ============================================================================
// 运维看板
// ============================================================================

export const getOpsStats = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/ops/stats`);

export const getOpsHealth = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/ops/health`);

export const listWorkItems = (params = {}) => {
    const query = {
        item_type: params.itemType || undefined,
        status: params.status || undefined,
        target_id: params.targetId || undefined,
        mailbox_receipt_id: params.mailboxReceiptId || undefined,
        result_summary: params.resultSummary || undefined,
        time_field: params.timeField || undefined,
        time_from: params.timeFrom || undefined,
        time_to: params.timeTo || undefined,
        page: params.page || 1,
        page_size: params.pageSize || 20,
    };
    return apiClient.get(`${MAIL_HUB_PREFIX}/ops/work-items`, { params: query });
};

export const getWorkItemDetail = (id) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/ops/work-items/${id}`);

export const getPipelineGovernance = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/ops/pipeline-governance`);

export const updatePipelineItemTypePolicy = (itemType, data) =>
    apiClient.patch(`${MAIL_HUB_PREFIX}/ops/pipeline-governance/item-types/${encodeURIComponent(itemType)}`, data);

export const updatePipelineTransitionPolicy = (transitionId, data) =>
    apiClient.patch(`${MAIL_HUB_PREFIX}/ops/pipeline-governance/transitions/${encodeURIComponent(transitionId)}`, data);

// ============================================================================
// 分类治理
// ============================================================================

export const listClassificationRules = () =>
    apiClient.get(`${MAIL_HUB_PREFIX}/classification-rules`);

export const getClassificationRule = (id) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/classification-rules/${id}`);

export const createClassificationRule = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/classification-rules`, data);

export const updateClassificationRule = (id, data) =>
    apiClient.patch(`${MAIL_HUB_PREFIX}/classification-rules/${id}`, data);

export const setClassificationRuleEnabled = (id, isEnabled) =>
    apiClient.patch(`${MAIL_HUB_PREFIX}/classification-rules/${id}/enabled`, {
        is_enabled: isEnabled,
    });

export const testClassificationRule = (data) =>
    apiClient.post(`${MAIL_HUB_PREFIX}/classification-rules/test`, data);

// ============================================================================
// 权限管理
// ============================================================================

/**
 * 查询指定邮箱关联的全部权限授予（含邮箱级与组织级）。
 */
export const listMailboxPermissions = (mailboxId) =>
    apiClient.get(`${MAIL_HUB_PREFIX}/permissions/mailbox/${mailboxId}`);

/**
 * 创建组织级发送授权（org-scope send_as grant）。
 *
 * organization_id 由 v3 façade 从 X-Active-Organization-Id header 解析后注入 JWT，
 * 前端无需传组织上下文。
 */
export const createOrgSendAsGrant = () =>
    apiClient.post(`${MAIL_HUB_PREFIX}/permissions`, { actions: ["send_as"] });

/**
 * 撤销一条权限授予。
 */
export const revokePermission = (grantId) =>
    apiClient.delete(`${MAIL_HUB_PREFIX}/permissions/${grantId}`);
